import React from 'react';

// Shared display helpers, previously copy-pasted across admin pages.
export const fmtDate = d => {
  if (!d) return '—';
  const dt = new Date(String(d).length <= 10 ? d + 'T00:00:00' : d);
  return isNaN(dt) ? '—' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const empName = u => `${u.firstName || u.first_name || ''} ${u.lastName || u.last_name || ''}`.trim() || u.name || u.username || '';

// The job number. Prefer a real project_number; else parse it from the name
// (legacy names start with "2106 - …" or "WO26901 - …"). Never surface the
// 8-char hex Trello board id that some legacy projects store as project_number.
export function jobNumber(p) {
  const pn = String((p && p.project_number) || '').trim();
  const isHex = /^[0-9a-f]{8}$/i.test(pn);
  if (pn && !isHex) return pn;
  const m = String((p && p.name) || '').match(/^\s*(WO\s?\d{3,6}|\d{3,6})/i);
  if (m) return m[1].replace(/\s+/g, '');
  return isHex ? '' : pn;
}

// A Date pinned to LOCAL midnight, for comparing calendar dates.
//
// A bare "2026-09-17" is a calendar date, but `new Date("2026-09-17")` parses it as
// UTC midnight per the JS spec — 5pm the previous day in Reno. That made a job due
// today bucket as overdue. fmtDate above already applies the same rule for display;
// this is for the arithmetic.
export function asLocalDate(value) {
  if (!value) return null;
  const s = String(value);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(s + 'T00:00:00') : new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

// The job name with any leading job-number prefix stripped, so the number isn't
// shown twice when it's already displayed as a badge. Accepts a project or a
// raw name string. "2106 - North Haiwee Dam" → "North Haiwee Dam".
export function jobName(p) {
  const raw = String(typeof p === 'string' ? p : ((p && p.name) || '')).trim();
  const stripped = raw
    // Work orders are named "<job>-WO### <what it's for>" with no separator, and the
    // rule below would strip only the "2633-" off that and leave a stray "WO004".
    // Take the whole combined number first.
    .replace(/^\s*\d{3,6}-WO\d{1,4}\s+/i, '')
    .replace(/^\s*(WO\s?\d{3,6}|\d{3,6})\s*[-–—:.]+\s*/i, '')
    .trim();
  return stripped || raw;
}

// "2106 — North Haiwee Dam" for dropdowns/labels, with no number duplication.
export const jobLabel = p => {
  const num = jobNumber(p);
  const nm = jobName(p);
  return [num, nm].filter(Boolean).join(' — ') || nm || num || 'Job';
};

// Placeholder card for features that aren't built out yet.
export function ComingSoon({ title, blurb, points }) {
  return (
    <div className="admin-card" style={{ padding: 40, textAlign: 'center', maxWidth: 620, margin: '0 auto' }}>
      <div style={{ fontSize: 40, marginBottom: 10 }}>🚧</div>
      <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700 }}>{title}</h2>
      <p style={{ color: '#6b7280', fontSize: 14, margin: '0 0 16px' }}>{blurb}</p>
      {points && (
        <ul style={{ textAlign: 'left', display: 'inline-block', color: '#6b7280', fontSize: 13, lineHeight: 1.8, margin: 0 }}>
          {points.map((p, i) => <li key={i}>{p}</li>)}
        </ul>
      )}
      <div style={{ marginTop: 20 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#9333ea', background: '#faf5ff', padding: '6px 14px', borderRadius: 20 }}>Coming soon — we'll build this out together</span>
      </div>
    </div>
  );
}

// ── Document naming ───────────────────────────────────────────────────────────
// One rule for everything we print or export, so a file is identifiable the moment
// it lands in someone's downloads or an email.
//
// Before the job exists it's a QUOTE, and what matters is which contractor it went
// to:                    "NDOT 4548 Q&D"
// Once it's a job or work order, the job number leads, then what the document is,
// its number, and a short description of which one:
//                        "2633 Change Order 01 Knocked-down pole"
//                        "2633 Submittal 04 Pull Boxes"
// A reprint that supersedes an earlier copy gets " rev2" on the end — set
// deliberately when it matters, not counted automatically.

// Strip anything a file system or mail client will argue about. Keeps & and #,
// which turn up constantly in company names and job numbers.
const safeFilePart = s => String(s || '')
  .replace(/[\\/:*?"<>|]/g, ' ')   // illegal on Windows
  .replace(/\s+/g, ' ')
  .trim();

// "Q&D Construction" → "Q&D".  "Granite Construction Company" → "Granite".
export const shortCompany = name => safeFilePart(name).split(' ')[0] || '';

// A quote, before award: bid name + who it's going to.
export function quoteFileName(bid, recipient, ext) {
  const job = safeFilePart(bid?.job_name || 'Quote');
  const to = shortCompany(recipient?.client_name || recipient?.name || bid?.client_name);
  const base = [job, to].filter(Boolean).join(' ');
  return ext ? `${base}.${ext}` : base;
}

// Anything printed off a job or work order.
//   number      the document's own number (CO 1 → "01"); omitted when there isn't one
//   description short reminder of which one — the CO title, the submittal category
//   rev         2, 3 … for a reprint that replaces an earlier copy
export function jobDocFileName({ job, type, number, description, rev, ext }) {
  const num = job?.project_number || job?.job_number || '';
  const n = number == null || number === '' ? '' : String(number).padStart(2, '0');
  const base = [safeFilePart(num), safeFilePart(type), n, safeFilePart(description).slice(0, 40)]
    .filter(Boolean).join(' ') + (rev && Number(rev) > 1 ? ` rev${Number(rev)}` : '');
  return ext ? `${base}.${ext}` : base;
}
